import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Zap,
  MonitorSmartphone,
  DollarSign,
  Sparkles,
  Globe,
  LifeBuoy,
  Play,
  ShieldCheck,
  Download,
  ChevronRight,
  Menu as MenuIcon,
  X as CloseIcon,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    Icon: Zap,
    title: "Fast Online Viewing",
    body: "Lightning-fast HLS/MPEG-TS playback with minimal buffering — even on slow networks.",
  },
  {
    Icon: MonitorSmartphone,
    title: "Every Device",
    body: "Android, iPhone, iPad, Apple TV, Windows, macOS, Samsung, LG, Roku, Fire TV, Google TV, VIDAA.",
  },
  {
    Icon: DollarSign,
    title: "Flexible Pricing",
    body: "Monthly Package · One-Year Package · Lifetime Package. Choose the plan that works best for you. Upgrade anytime.",
  },
  {
    Icon: Sparkles,
    title: "Beautiful Interface",
    body: "TV-first design with focus rings, dark theme, and thoughtful animation on every screen.",
  },
  {
    Icon: Globe,
    title: "Multilingual",
    body: "Full interface localisation across English, Español, Français, Deutsch, Nederlands, and more.",
  },
  {
    Icon: LifeBuoy,
    title: "24/7 Support",
    body: "Real humans, not chatbots — replies within 4 hours on every business day.",
  },
];

const APP_STORES = [
  { id: "google_play", label: "Google Play",       src: "https://vuplayer.pro/assets/apps/store/google_play.png", alt: "Get it on Google Play" },
  { id: "apple_app_store", label: "App Store",     src: "https://vuplayer.pro/assets/apps/store/ios.png",         alt: "Download on the App Store" },
  { id: "lg",          label: "LG Content Store",  src: "https://vuplayer.pro/assets/apps/store/lg.png",          alt: "LG Content Store" },
  { id: "samsung",     label: "Samsung Apps",      src: "https://vuplayer.pro/assets/apps/store/samsung.png",     alt: "Samsung Apps" },
  { id: "roku",        label: "Roku Channel Store",src: "https://vuplayer.pro/assets/apps/store/roku.png",        alt: "Roku Channel Store" },
  { id: "microsoft",   label: "Microsoft Store",   src: "https://vuplayer.pro/assets/apps/store/windows.png",     alt: "Microsoft Store" },
  { id: "vidaa",       label: "VIDAA Store",       src: "https://vuplayer.pro/assets/apps/store/vidaa.png",       alt: "VIDAA Store" },
];

const HOW = [
  {
    n: "01",
    title: "Install the app",
    body: "Grab ZA Player Pro for your platform — every device below is a single download away.",
  },
  {
    n: "02",
    title: "Add your playlist",
    body: "Enter your M3U or Xtream Codes credentials, or use the activation key from your provider.",
  },
  {
    n: "03",
    title: "Activate your device",
    body: "One-tap device activation. Locked to your device — no logins, no accounts.",
  },
  {
    n: "04",
    title: "Enjoy!",
    body: "Live TV, Movies, Series, EPG, multiple profiles, Kids Mode, Chromecast, AirPlay. That's it.",
  },
];

// ---- Reusable bits ---------------------------------------------------------

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`w-full py-24 md:py-32 ${className}`} data-testid={`section-${id}`}>
    <div className="mx-auto max-w-6xl px-6">{children}</div>
  </section>
);

const SectionHeading = ({ eyebrow, title, sub }) => (
  <div className="mb-16 text-center">
    {eyebrow && (
      <div className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-400">
        {eyebrow}
      </div>
    )}
    <h2 className="mx-auto max-w-3xl text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">
      {title}
    </h2>
    {sub && <p className="mx-auto mt-4 max-w-2xl text-base text-neutral-400">{sub}</p>}
  </div>
);

const StoreBadge = ({ src, alt, label, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group inline-flex items-center justify-center overflow-hidden rounded-2xl bg-neutral-900 p-3 ring-1 ring-white/10 transition-all hover:scale-[1.03] hover:bg-neutral-800 hover:ring-emerald-400/40"
    aria-label={label}
    data-testid={`store-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <img src={src} alt={alt} className="h-14 w-40 object-contain" loading="lazy" />
  </a>
);

// ---- Landing Page ----------------------------------------------------------

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    document.title = "ZA Player Pro — M3U & Xtream Player for every device";
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fetch admin-editable website config (store URLs, direct downloads, legal links)
    fetch(`${BACKEND}/api/public/website-config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setCfg(data))
      .catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Only render store badges that have a URL configured
  const storesToShow = APP_STORES
    .map((s) => ({ ...s, href: cfg?.[`store_${s.id}`] || "" }))
    .filter((s) => s.href);

  // Support link resolver: if the admin has left `cfg.support_url`
  // pointing at our own /support page (either as a relative path or as
  // an absolute URL against zaplayerpro.club / the current host), we
  // navigate INTERNALLY. That way the button works on the preview URL,
  // on the production domain, and inside the native app wrappers —
  // without depending on DNS. Only genuinely external URLs (Telegram,
  // Discord, ticket portals) open in a new tab.
  const resolveSupport = () => {
    const raw = (cfg?.support_url || "").trim();
    if (!raw) return { href: "/support", label: "Support", internal: true };
    try {
      const u = new URL(raw, typeof window !== "undefined" ? window.location.origin : "https://zaplayerpro.club");
      const sameOrigin =
        typeof window !== "undefined" && u.host === window.location.host;
      const ourHost = u.host === "zaplayerpro.club" || u.host === "www.zaplayerpro.club";
      if ((sameOrigin || ourHost) && u.pathname.replace(/\/$/, "") === "/support") {
        return { href: "/support", label: "Support", internal: true };
      }
      return { href: raw, label: "Support", external: true };
    } catch {
      // Fallback: treat as an internal path.
      return { href: raw.startsWith("/") ? raw : "/support", label: "Support", internal: true };
    }
  };

  const nav = [
    { href: "#features", label: "Features" },
    { href: "#download", label: "Download" },
    { href: "#how", label: "How it works" },
    { href: "/trial", label: "Free Trial", internal: true },
    { href: "/activation", label: "Activation", internal: true },
    { href: "/playlist", label: "Playlist", internal: true },
    { href: "/admin/login", label: "Reseller", internal: true },
    resolveSupport(),
  ];

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      {/* --- Header --- */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-black/85 backdrop-blur-md ring-1 ring-white/10" : "bg-transparent"
        }`}
        data-testid="landing-header"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/za-logo-icon.png" alt="ZA Player Pro" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold tracking-tight">ZA Player Pro</span>
          </a>

          <nav className="hidden items-center gap-8 md:flex" data-testid="desktop-nav">
            {nav.map((n) =>
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  className="text-sm text-neutral-300 transition-colors hover:text-emerald-400"
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  target={n.external ? "_blank" : undefined}
                  rel={n.external ? "noopener noreferrer" : undefined}
                  className="text-sm text-neutral-300 transition-colors hover:text-emerald-400"
                >
                  {n.label}
                </a>
              )
            )}
          </nav>

          <button
            className="md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <CloseIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-white/10 bg-black/95 md:hidden" data-testid="mobile-nav">
            <nav className="mx-auto flex max-w-6xl flex-col gap-2 px-6 py-4">
              {nav.map((n) =>
                n.internal ? (
                  <Link
                    key={n.href}
                    to={n.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-neutral-200 hover:bg-white/5"
                  >
                    {n.label}
                  </Link>
                ) : (
                  <a
                    key={n.href}
                    href={n.href}
                    target={n.external ? "_blank" : undefined}
                    rel={n.external ? "noopener noreferrer" : undefined}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-neutral-200 hover:bg-white/5"
                  >
                    {n.label}
                  </a>
                )
              )}
            </nav>
          </div>
        )}
      </header>

      {/* --- Hero --- */}
      <Section id="hero" className="relative overflow-hidden pt-40">
        {/* radial background glow */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-24 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_60%)]" />
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              ZA Player Pro
              <span className="mt-3 block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Watch anything. Anywhere.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg" data-testid="hero-sub">
              A cinematic M3U &amp; Xtream Codes player designed for
              high-quality viewing across every platform you own. Bring
              your own playlist — we do the rest.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
              <a href="#download">
                <Button
                  size="lg"
                  className="rounded-full bg-emerald-500 px-6 py-6 text-base font-semibold text-black hover:bg-emerald-400"
                  data-testid="hero-download-btn"
                >
                  <Download className="mr-2 h-5 w-5" /> Download Now
                </Button>
              </a>
              <Link to="/trial">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-emerald-400/40 bg-emerald-500/10 px-6 py-6 text-base font-semibold text-emerald-300 backdrop-blur hover:bg-emerald-500/20 hover:text-emerald-200"
                  data-testid="hero-trial-btn"
                >
                  <Sparkles className="mr-2 h-5 w-5" /> Try free — 5 days
                </Button>
              </Link>
              <a href="#how">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-white/20 bg-white/5 px-6 py-6 text-base font-semibold text-white backdrop-blur hover:bg-white/10"
                  data-testid="hero-tutorial-btn"
                >
                  <Play className="mr-2 h-5 w-5" /> Watch Tutorial
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-neutral-400">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> No ads, ever</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Zero tracking</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Player-only</span>
            </div>
          </div>

          <div className="relative">
            <div className="relative w-full overflow-visible">
              <img
                src="/hero-tv.png"
                alt="ZA Player Pro on TV"
                className="h-auto w-full drop-shadow-[0_40px_120px_rgba(74,222,128,0.15)]"
                loading="eager"
                data-testid="hero-tv-image"
              />
            </div>
          </div>
        </div>

        {/* Legal disclaimer strip */}
        <div className="mt-24 rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-6 text-center text-sm leading-relaxed text-yellow-100/80" data-testid="disclaimer">
          We do not sell, provide, or distribute any content, streams, or channels — whether legal or illegal — including movies or series. We strongly recommend that users use their own legally obtained playlists or create their own content lists. The use of unauthorized or illegal content is strictly discouraged.
        </div>
      </Section>

      {/* --- Features --- */}
      <Section id="features" className="border-t border-white/5">
        <SectionHeading
          eyebrow="Why ZA Player Pro"
          title="Built for people who take their online viewing seriously"
          sub="Every screen has been designed for a 10-foot experience. Every codec tuned. Every animation earned."
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="feature-grid">
          {FEATURES.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 p-8 transition-all hover:border-emerald-400/30 hover:-translate-y-1"
              data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-white">{title}</h3>
              <p className="text-sm leading-relaxed text-neutral-400">{body}</p>
              <div className="pointer-events-none absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-emerald-500/5 blur-2xl transition-opacity group-hover:opacity-100" />
            </div>
          ))}
        </div>
      </Section>

      {/* --- Download --- */}
      <Section id="download" className="border-t border-white/5">
        <SectionHeading
          eyebrow="Download"
          title="Available on every screen you own"
          sub="Experience seamless online viewing — your ultimate destination for downloading and enjoying high-quality video, everywhere."
        />

        {storesToShow.length > 0 ? (
          <div className="grid grid-cols-2 items-center gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4" data-testid="store-grid">
            {storesToShow.map((s) => (
              <StoreBadge key={s.id} {...s} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-950 p-8 text-center text-sm text-neutral-500" data-testid="store-grid-empty">
            App-store links haven&apos;t been published yet.
            {" "}<Link to="/trial" className="text-emerald-400 underline">Start a free 5-day trial</Link>
            {" "}or use the direct downloads below.
          </div>
        )}

        {/* Direct downloads (only render tiles that have a configured URL) */}
        <div className="mt-16 grid gap-6 md:grid-cols-3" data-testid="direct-downloads">
          {[
            { key: "direct_apk_url", label: "Direct APK", subtitle: "Android / Fire TV / Google TV" },
            { key: "direct_exe_url", label: "Direct EXE", subtitle: "Windows 10 / 11" },
            { key: "direct_ipk_url", label: "Sideload",   subtitle: "LG webOS · Samsung .wgt" },
          ].map((tile) => {
            const href = cfg?.[tile.key] || "";
            const disabled = !href;
            return (
              <a
                key={tile.key}
                href={href || undefined}
                className={`flex items-center gap-4 rounded-2xl border border-white/10 bg-neutral-950 p-6 transition-all ${
                  disabled
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:border-emerald-400/30 hover:-translate-y-1"
                }`}
                data-testid={`direct-${tile.key}`}
                aria-disabled={disabled}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <Download className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">{tile.label}</div>
                  <div className="text-sm font-semibold">{tile.subtitle}</div>
                </div>
              </a>
            );
          })}
        </div>
      </Section>

      {/* --- How it works --- */}
      <Section id="how" className="border-t border-white/5">
        <SectionHeading
          eyebrow="Workflow"
          title="How to use ZA Player Pro"
          sub="Simple, fast, and just as opinionated as it needs to be."
        />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="how-grid">
          {HOW.map((step, i) => (
            <div
              key={step.n}
              className="relative rounded-2xl border border-white/10 bg-neutral-950 p-8"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="mb-4 text-4xl font-bold text-emerald-400">{step.n}</div>
              <h3 className="mb-2 text-lg font-semibold">{step.title}</h3>
              <p className="text-sm text-neutral-400">{step.body}</p>
              {i < HOW.length - 1 && (
                <ChevronRight className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-white/30 lg:block" />
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* --- CTA --- */}
      <Section id="cta" className="border-t border-white/5">
        <div className="relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 p-10 text-center md:p-16" data-testid="cta-banner">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to watch?</h2>
          <p className="mx-auto mt-3 max-w-xl text-neutral-300">
            Pick a platform, install the app, activate once. That&apos;s it — the rest is popcorn.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href="#download">
              <Button size="lg" className="rounded-full bg-emerald-500 px-6 py-6 text-base font-semibold text-black hover:bg-emerald-400" data-testid="cta-download-btn">
                <Download className="mr-2 h-5 w-5" /> Download the App
              </Button>
            </a>
            <Link to="/trial">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-white/20 bg-white/5 px-6 py-6 text-base font-semibold text-white backdrop-blur hover:bg-white/10"
                data-testid="cta-trial-btn"
              >
                <Sparkles className="mr-2 h-5 w-5" /> Try free — 5 days
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* --- Footer --- */}
      <footer className="border-t border-white/5 py-12" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-3">
                <img src="/za-logo-icon.png" alt="ZA Player Pro" className="h-8 w-8 rounded-lg" />
                <span className="font-bold">ZA Player Pro</span>
              </div>
              <p className="mt-3 text-sm text-neutral-400">
                A cinematic M3U &amp; Xtream player. Bring your own playlist.
              </p>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-widest text-neutral-500">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-neutral-300 hover:text-emerald-400">Features</a></li>
                <li><a href="#download" className="text-neutral-300 hover:text-emerald-400">Download</a></li>
                <li><Link to="/trial" className="text-neutral-300 hover:text-emerald-400">Free Trial</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-widest text-neutral-500">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/support" className="text-neutral-300 hover:text-emerald-400">Support</a></li>
                <li><a href={`mailto:${cfg?.contact_email || "support@zaplayerpro.club"}`} className="text-neutral-300 hover:text-emerald-400">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-widest text-neutral-500">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy" className="text-neutral-300 hover:text-emerald-400">Privacy Policy</a></li>
                <li><a href="/terms" className="text-neutral-300 hover:text-emerald-400">Terms &amp; Conditions</a></li>
                <li><a href="/refund" className="text-neutral-300 hover:text-emerald-400">Refund Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-6 text-xs text-neutral-500 md:flex-row">
            <div>© {new Date().getFullYear()} ZA Player Pro. All rights reserved.</div>
            <div>Player-only application. No content included or supplied.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
